import { useRef, useEffect, useMemo } from 'react';
import { Outlet, NavLink } from 'react-router-dom';
import {
  IconExpand,
  useQueryState,
  useGetList,
  useScroll,
  Table,
  usePrevious,
  IconLoader,
  useFilters,
  PermissionEnum,
  PermissionDenied,
} from '@ui/src';
import { useAppSelector } from '@store';
import {
  clientsActivitiesListSchema,
  clientsActivitiesListColumns,
  clientsActivitiesListFiltersSchema,
} from '../variables/clients-activities';
import { useLazyGetClientsActivitiesListQuery } from '../store/services';
import { RiskScoreItem, ClientsActivity } from '../../types';
import {
  useLazyGetAmlDictionariesQuery,
  useLazyGetAmlBaseCurrenciesQuery,
  useLazyGetAmlCurrenciesQuery,
} from '../../../store/dictionaries/services';
import ROUTES from '../../../routes';

import cn from '../individual-clients-risk.module.css';

const formatClientsActivitiesList = (
  list: ClientsActivity[],
): ClientsActivity[] => {
  const nextList = list?.map((item) => {
    const result = { ...item };

    const render: Record<string, React.JSX.Element> = {
      riskCategory: (
        <div>{`${item.riskCategory.charAt(0)}${item.riskCategory
          .slice(1)
          .toLowerCase()} risk`}</div>
      ),
    };

    result.render = render;

    return result;
  });

  return nextList;
};

const ClientsActivitiesList = () => {
  const scrollRef = useRef<HTMLDivElement | null>(null);

  const { values, pagination, errorsList } = useAppSelector(
    (state) => state.individualClientsRisk.clientActivity.list,
  );

  const { activePermissions, riskScoreRanges, currencies, baseCurrency } =
    useAppSelector((state) => state.amlDictionaries);

  const { sort, onSortChange, filters, onFilterChange } = useQueryState(
    clientsActivitiesListSchema,
  );

  const [getDictionaries] = useLazyGetAmlDictionariesQuery();
  const [getAmlBaseCurrency] = useLazyGetAmlBaseCurrenciesQuery();
  const [getAmlCurrencies] = useLazyGetAmlCurrenciesQuery();

  const prevFilters = usePrevious(filters);

  const [getClientsActivities, { isLoading, isFetching }] =
    useLazyGetClientsActivitiesListQuery();

  const { getListAction, setPage, onSort, onFilter, errorList } =
    useGetList<RiskScoreItem>({
      getList: getClientsActivities,
      onFilterChange,
      onSortChange,
      schema: clientsActivitiesListSchema,
    });

  const formattedData = useMemo(() => {
    const data = formatClientsActivitiesList(values as ClientsActivity[]);

    return data;
  }, [values, sort]);

  const { filtersComponent, handleSubmit } = useFilters({
    filtersSchema: clientsActivitiesListFiltersSchema,
    filters,
    onFilterChange: onFilter,
  });

  useScroll({
    element: scrollRef,
    requestInProgress: isFetching,
  });

  useEffect(() => {
    if (
      prevFilters &&
      !Object.keys(prevFilters).length &&
      filters &&
      !values &&
      sort &&
      Object.keys(sort).length &&
      activePermissions?.includes(
        PermissionEnum.SETTINGS_CLIENT_ACTIVITIES_LIST,
      )
    ) {
      getListAction({
        filters,
        sort,
      });
    }
  }, [filters, prevFilters, sort, values, activePermissions]);

  useEffect(() => {
    if (
      activePermissions?.includes(
        PermissionEnum.SETTINGS_CLIENT_ACTIVITIES_LIST,
      )
    ) {
      if (!riskScoreRanges) {
        getDictionaries();
      }

      if (!currencies) {
        getAmlCurrencies();
      }

      if (!baseCurrency) {
        getAmlBaseCurrency();
      }
    }
  }, [activePermissions, riskScoreRanges, currencies, baseCurrency]);

  useEffect(() => {
    let title = 'Activity of the client';

    const companyName = localStorage.getItem('companyName');

    if (companyName) {
      title = `${title} | ${companyName}`;
    }

    document.title = title;
  }, []);

  let content;
  let globalContent = (
    <div className={cn.clientsActivitiesLoader}>
      <IconLoader />
    </div>
  );

  if (activePermissions) {
    if (
      activePermissions.includes(PermissionEnum.SETTINGS_CLIENT_ACTIVITIES_LIST)
    ) {
      if (isLoading) {
        content = (
          <div className={cn.clientsActivitiesLoader}>
            <IconLoader />
          </div>
        );
      } else {
        content = (
          <Table
            columns={clientsActivitiesListColumns}
            data={formattedData || []}
            onSort={(name) => onSort(name)}
            filters={filters}
            sort={sort}
            columnsLabels={{}}
            onFilterChange={(filtersValues) => onFilter(filtersValues)}
            filtersComponent={filtersComponent}
            handleSubmit={handleSubmit}
            isLoading={isFetching}
            take="30"
            error={errorsList || errorList}
            setPage={(data) => setPage({ ...data, filters })}
            pagination={pagination}
            clickable
          />
        );
      }

      globalContent = (
        <div className={cn.clientsActivities}>
          <div className={cn.clientsActivitiesBottom}>
            <div className={cn.clientsActivitiesControls}>
              {activePermissions?.includes(
                PermissionEnum.SETTINGS_CLIENT_ACTIVITIES_CREATE_ACTION,
              ) && (
                <NavLink
                  to={ROUTES.INDIVIDUAL_CLIENTS_RISK_ACTIVITIES_CREATE}
                  className={cn.clientsActivitiesLink}
                >
                  <IconExpand />
                  Add activity
                </NavLink>
              )}
            </div>
            <div className={cn.clientsActivitiesTableWrapper} ref={scrollRef}>
              {content}
            </div>
            <Outlet />
          </div>
        </div>
      );
    } else {
      globalContent = <PermissionDenied />;
    }
  }

  return globalContent;
};

export default ClientsActivitiesList;
